<template>
  <tab-item-template>
    <template #toolbar-items>
      <icon-btn
          tooltip="取引先 CSV エクスポート"
          icon="mdi-cloud-download-outline"
          @click="exportTerminals">
      </icon-btn>
      <v-spacer></v-spacer>
      <toolbar-inline>
        <v-checkbox
            label="取引先のみ表示"
            v-model="showSelected"
            class="mr-2">
        </v-checkbox>
      </toolbar-inline>
      <toolbar-inline>
        <v-text-field
            v-model="filterText"
            clearable
            placeholder="検索">
        </v-text-field>
      </toolbar-inline>
    </template>

    <el-table
        :data="ownersView"
        border>
      <el-table-column
          label="取引先"
          width="80"
          column-key="isSelected"
          align="center">
        <template #default="{row}">
          <table-inline>
            <v-progress-circular
                v-if="row.customer.loading"
                size="16"
                width="2"
                indeterminate>
            </v-progress-circular>
            <el-checkbox
                v-else
                v-model="row.customer.isSelected"
                @change="handleSelectedChange(row)">
            </el-checkbox>
          </table-inline>
        </template>
      </el-table-column>
      <el-table-column
          label="ID"
          width="140"
          prop="id">
      </el-table-column>
      <el-table-column
          label="事業者名"
          width="200"
          prop="name"
          sortable>
      </el-table-column>
      <el-table-column
          label="所在地"
          width="120"
          prop="location"
          sortable>
      </el-table-column>
      <el-table-column
          label="説明"
          prop="description"
          sortable>
        <template #default="{row}">
          <table-inline>
            <text-field-like
                :value="row.description"
                placeholder="未設定"
                multi-line>
            </text-field-like>
          </table-inline>
        </template>
      </el-table-column>
      <el-table-column
          width="70"
          align="center"
          fixed="right"
          column-key="operations"
          class-name="operations">
        <template #default="{row}">
          <icon-btns>
            <icon-btn
                type="view"
                @click="ownerInfoDialog = row.id">
            </icon-btn>
          </icon-btns>
        </template>
      </el-table-column>
    </el-table>

    <owner-info-dialog
        v-model="ownerInfoDialog">
    </owner-info-dialog>
  </tab-item-template>
</template>

<script>
import TabItemTemplate from "@/components/templates/TabItemTemplate.vue";
import {mapGetters, mapMutations} from "vuex";
import TableInline from "@/components/atoms/TableInline.vue";
import IoablePageMixin from "@/router/IoablePageMixin";
import OwnerInfoDialog from "@/components/organisms/OwnerInfoDialog.vue";
import IconBtns from "@/components/molecules/IconBtns";
import IconBtn from "@/components/molecules/IconBtn.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import ToolbarInline from "@/components/atoms/ToolbarInline.vue";
import PageStoreMixin, {mapPageStores} from "@/utils/PageStoreMixin";
export default {
  name: "OwnerCustomerTab",
  components: {ToolbarInline, TextFieldLike, IconBtn, IconBtns, OwnerInfoDialog, TableInline, TabItemTemplate},
  mixins: [
      IoablePageMixin,
      PageStoreMixin,
  ],
  data(){
    return {
      owners: [],
      filterText: '',
      ownerInfoDialog: false,
    };
  },
  computed: {
    ...mapGetters({
      account: 'hcs/account',
      hcsClient: 'hcs/client',
    }),
    ...mapPageStores({
      showSelected: {alt: false}
    }),
    ownersView() {
      let owners = this.owners;
      const filterText = this.filterText;
      if(filterText) {
        owners = owners.filter(o =>
          o.name?.includes(filterText) ||
          o.location?.includes(filterText) ||
          o.description?.includes(filterText)
        );
      }
      if(this.showSelected) {
        owners = owners.filter(o => o.customer.isSelectedForFilter);
      }
      return owners;
    },
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar',
    }),
    async load() {
      const ownerCustomerMap = new Map();
      const {data: ownerCustomers} = await this.hcsClient.get('/owner_customers');
      ownerCustomers.forEach(oc => {
        ownerCustomerMap.set(oc.customerId, {
          id: oc.id,
          isSelected: !oc.isDeleted,
          isSelectedInitial: !oc.isDeleted,
          isSelectedForFilter: !oc.isDeleted,
          loading: false,
        });
      });

      const {data: owners} = await this.hcsClient.get('/assets/owners');
      this.owners = owners
          .filter(o => o.id !== this.account.owner.id)
          .map(o => ({
            ...o,
            customer: ownerCustomerMap.get(o.id) || {
              id: null,
              isSelected: false,
              isSelectedInitial: false,
              isSelectedForFilter: false,
              loading: false}
          }));
    },
    async handleSelectedChange(owner) {
      const c = owner.customer;
      try {
        c.loading = true;
        const timer = new Promise(resolve => {
          setTimeout(resolve, 500);
        });
        if (c.id === null && c.isSelected) {
          await this.hcsClient.post('/owner_customers', {customerId: owner.id, isDeleted: false});
        } else if (c.id !== null && c.isSelected !== c.isSelectedInitial) {
          await this.hcsClient.patch(`/owner_customers/${c.id}`, {isDeleted: !c.isSelected});
          c.isSelectedInitial = c.isSelected;
        }
        await timer;
      } finally {
        c.loading = false;
      }
    },
    async exportTerminals() {
      const toRow = (o, t) => ([
        t.id,
        t.name,
        t.description,
        o.id,
        o.name,
        o.nameKatakana,
        o.nameEnglish,
        o.description,
      ]);
      const filename = 'customers.csv';
      const rows = [];
      const loading = this.$loading({
        lock: true,
        spinner: 'el-icon-loading',
      });
      try {
        // データ読み込み
        const {data: assetsTerminals} = await this.hcsClient.get('/assets/customer_terminals');
        const {data: ourTerminals} = await this.hcsClient.get('/terminals');
        rows.push.apply(rows, assetsTerminals.map(t => toRow(t.owner, t)));
        rows.push.apply(rows, ourTerminals.map(t => toRow(t.owner, t)));
        rows.unshift([
          'サイトID',
          'サイト名',
          'サイト説明',
          '事業者ID',
          '事業者名',
          '事業者名(カナ)',
          '事業者名(英語)',
          '事業者説明'
        ]);

        // CSV 出力
        const csv = rows.map(r => r.map(r => !r ? '' : r).map(c => `"${c}"`).join(',')).join("\r\n");
        const bom  = new Uint8Array([0xEF, 0xBB, 0xBF]);
        const blob = new Blob([bom, csv], {type: 'text/csv'});
        const url = (window.URL || window.webkitURL).createObjectURL(blob);
        const link = document.createElement('a');
        link.download = filename;
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } finally {
        loading.close();
      }
    }
  },
  watch: {
    showSelected(value) {
      if(value) {
        for(const owner of this.owners) {
          const c = owner.customer;
          c.isSelectedForFilter = c.isSelected;
        }
      }
    }
  }
}
</script>

<style scoped>

</style>
